import apiClient from './api';
import type { Parking, StatutParking, SituationParking, PaginatedResponse } from '@/types';

const BASE_URL = '/parkings';

export interface CreateParkingDTO {
  code: string;
  nom?: string;
  aeroport: string;
  statutHiver?: StatutParking;
  situation?: SituationParking;
  latitude?: number | null;
  longitude?: number | null;
}

export interface UpdateParkingDTO extends Omit<Partial<CreateParkingDTO>, 'situation'> {
  actif?: boolean;
  situation?: SituationParking | null;
}

export interface ParkingFilters {
  aeroport?: string;
  statutHiver?: StatutParking;
  actif?: boolean;
  /** 'sans_situation' pour filtrer les parkings sans situation renseignee */
  situation?: SituationParking | 'sans_situation';
}

export interface ParkingStats {
  total: number;
  disponibles: number;
  enDeneignement: number;
  indisponibles: number;
  /** SFD jalonnement Contact/Large : parkings actifs sans situation renseignee */
  sansSituation: number;
}

export interface ImportResult {
  created: number;
  updated: number;
  errors: string[];
}

/**
 * Service API pour les parkings
 */
export const parkingService = {
  /**
   * Liste des parkings avec filtres
   */
  async list(filters: ParkingFilters = {}): Promise<Parking[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<Parking[]>(
      `${BASE_URL}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Detail d'un parking
   */
  async get(id: number): Promise<Parking> {
    const response = await apiClient.get<Parking>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Creation d'un parking
   */
  async create(data: CreateParkingDTO): Promise<Parking> {
    const response = await apiClient.post<Parking>(BASE_URL, {
      code: data.code,
      nom: data.nom,
      aeroport: data.aeroport,
      statut_hiver: data.statutHiver || 'disponible',
      situation: data.situation,
      latitude: data.latitude,
      longitude: data.longitude,
    });
    return response.data;
  },

  /**
   * Modification d'un parking
   */
  async update(id: number, data: UpdateParkingDTO): Promise<Parking> {
    const payload: Record<string, unknown> = {};
    if (data.code !== undefined) payload.code = data.code;
    if (data.nom !== undefined) payload.nom = data.nom;
    if (data.aeroport !== undefined) payload.aeroport = data.aeroport;
    if (data.statutHiver !== undefined) payload.statut_hiver = data.statutHiver;
    if (data.actif !== undefined) payload.actif = data.actif;
    if (data.situation !== undefined) payload.situation = data.situation;
    if (data.latitude !== undefined) payload.latitude = data.latitude;
    if (data.longitude !== undefined) payload.longitude = data.longitude;

    const response = await apiClient.put<Parking>(`${BASE_URL}/${id}`, payload);
    return response.data;
  },

  /**
   * Suppression d'un parking
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Modifier le statut hiver
   */
  async updateStatutHiver(id: number, statut: StatutParking): Promise<Parking> {
    const response = await apiClient.patch<Parking>(
      `${BASE_URL}/${id}/statut-hiver`,
      { statut_hiver: statut }
    );
    return response.data;
  },

  /**
   * Parkings disponibles
   */
  async getDisponibles(): Promise<Parking[]> {
    const response = await apiClient.get<Parking[]>(`${BASE_URL}/disponibles`);
    return response.data;
  },

  /**
   * Liste des aeroports
   */
  async getAeroports(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${BASE_URL}/aeroports/list`);
    return response.data;
  },

  /**
   * Statistiques globales. La reponse API utilise des cles differentes
   * (`par_statut.disponible/en_deneignement/indisponible`, `sans_situation`) -
   * remappees ici vers la forme attendue par le frontend. Corrige au passage
   * un bug preexistant : la lecture directe de `response.data` sans ce
   * remappage ne renvoyait que `total`, les 3 compteurs par statut etant
   * toujours `undefined` dans les cartes de la page Parkings.
   */
  async getStats(): Promise<ParkingStats> {
    const response = await apiClient.get<{
      total: number;
      par_statut: Record<StatutParking, number>;
      sans_situation: number;
    }>(`${BASE_URL}/stats/global`);
    const data = response.data;

    return {
      total: data.total,
      disponibles: data.par_statut?.disponible ?? 0,
      enDeneignement: data.par_statut?.en_deneignement ?? 0,
      indisponibles: data.par_statut?.indisponible ?? 0,
      sansSituation: data.sans_situation ?? 0,
    };
  },

  /**
   * Import depuis un fichier Excel ou CSV. `aeroport` : valeur par defaut
   * utilisee pour les lignes qui n'ont pas de colonne aeroport (cas des
   * referentiels AIP/SIA type "postes LFPO", un seul aeroport par fichier).
   */
  async import(file: File, aeroport?: string): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (aeroport) {
      formData.append('aeroport', aeroport);
    }
    const response = await apiClient.post<ImportResult>(`${BASE_URL}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default parkingService;
